import { FC, PropsWithChildren } from 'react'

import { ErrorRouteComponent } from '@tanstack/react-router'

import {
  Card,
  Cluster,
  Skeleton,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from '@forto-dev/phoenix-ui-core'
import { Layout } from '@forto-dev/phoenix-ui-core/ship'

import { Back } from '@/fragments/back'
import { createErrorFallback } from '@/fragments/create-error-fallback'
import { Footer } from '@/fragments/footer'
import { Header } from '@/fragments/header'
import { Navigation } from '@/fragments/navigation'
import { useLocalisation } from '@/utils/localisation'

import { CompanyAccountSidebar } from './sidebar'

export const CompanyAccountTabsSkeleton: FC = () => {
  return (
    <Stack gap={24}>
      <Card>
        <Skeleton variant="badge" />
        <Skeleton variant="button" />
        <Skeleton variant="badge" />
      </Card>
      <Card>
        <Skeleton variant="badge" />
        <Skeleton variant="card" />
        <Skeleton variant="button" />
      </Card>
      <Card>
        <Skeleton variant="badge" />
        <Skeleton variant="card" />
        <Skeleton variant="button" />
      </Card>
      <Card>
        <Skeleton variant="badge" />
        <Skeleton variant="card" />
        <Skeleton variant="button" />
      </Card>
      <Card>
        <Skeleton variant="badge" />
        <Skeleton variant="card" />
        <Cluster gap={16}>
          <Skeleton variant="button" />
          <Skeleton variant="button" />
        </Cluster>
      </Card>
    </Stack>
  )
}

const CompanyAccountLayoutSkeleton: FC<PropsWithChildren> = ({ children }) => {
  const { translate } = useLocalisation()

  return (
    <Layout
      header={<Header />}
      navigation={<Navigation />}
      title={
        <Cluster y="center" x="between">
          <Typography variant="h4">{translate('company-account.title')}</Typography>
          <Cluster gap={8} y="center">
            <Skeleton variant="button" />
            <Skeleton variant="icon" />
          </Cluster>
        </Cluster>
      }
      sidebar={<CompanyAccountSidebar />}
      main={children}
      footer={<Footer />}
    />
  )
}

export const CompanyAccountSkeleton: FC = () => {
  return (
    <CompanyAccountLayoutSkeleton>
      <Stack>
        <Tabs value="0">
          <Stack gap={16}>
            <TabsList>
              <TabsTrigger value="0">
                <Skeleton variant="badge" />
              </TabsTrigger>
            </TabsList>
            <TabsContent value="0">
              <CompanyAccountTabsSkeleton />
            </TabsContent>
          </Stack>
        </Tabs>
      </Stack>
    </CompanyAccountLayoutSkeleton>
  )
}

export const CompanyAccountErrorComponent: ErrorRouteComponent = ({ error, reset }) => {
  return (
    <Layout
      header={<Header />}
      navigation={<Back to="/" />}
      main={createErrorFallback(`An error occurred while loading Company Account Page`)({ error, reset })}
      footer={<Footer />}
    />
  )
}
